import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { generateTempTagExternalFormSchema,TempTagIssuanceWorkflow } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Group, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useGenerateTempTag } from '../../../api/progress/useGenerateTempTag';
import { useGetGenerateTempTag } from '../../../api/progress/useGetGenerateTempTag';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface GenerateTempTagExternalDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * Gets workflow-specific instructional copy based on the issuance workflow type
 */
const getWorkflowInstructions = (workflow: TempTagIssuanceWorkflow | undefined): string => {
	switch (workflow) {
		case TempTagIssuanceWorkflow.BOOKLET:
			return 'Issue a temp tag using your temp tag booklet';
		case TempTagIssuanceWorkflow.EXTERNAL_PORTAL:
			return 'Issue a temp tag using the external state DMV portal';
		case TempTagIssuanceWorkflow.INTERNAL_PORTAL:
			return 'Issue a temp tag using your internal dealership portal/system';
		default:
			return 'Confirm that the temporary tag has been generated and applied to the vehicle.';
	}
};

/**
 * GenerateTempTagExternalDetail component for external temp tag workflows.
 * Displays a checkbox for confirmation with workflow-specific instructions and a submit button.
 * Used for BOOKLET, EXTERNAL_PORTAL, and INTERNAL_PORTAL workflows.
 */
export const GenerateTempTagExternalDetail = ({
	actionItem,
}: GenerateTempTagExternalDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const generateTempTagMutation = useGenerateTempTag(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetGenerateTempTag(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(generateTempTagExternalFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Get workflow-specific instructions
	const issuanceWorkflow = actionItem.metadata?.issuanceWorkflow as
		| TempTagIssuanceWorkflow
		| undefined;
	const workflowInstructions = getWorkflowInstructions(issuanceWorkflow);

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.tempTagApplied) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await generateTempTagMutation.mutateAsync({
				isConfirmed: true,
			});
			toast({
				message: 'Temp tag applied successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to apply temp tag';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions || workflowInstructions}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.tempTagApplied ? (
					<Text size='body' color='success'>
						✓ Temp tag already applied
					</Text>
				) : null}

				{/* Show form if not complete */}
				{!existingData?.tempTagApplied && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={isSubmitting || generateTempTagMutation.isPending}
											ariaLabel='Confirm temp tag issued'
										/>
										<Text size='bodySmall'>Confirm temp tag has been issued</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || generateTempTagMutation.isPending}
							isDisabled={isLoadingData}>
							{'Mark Temp Tag Applied'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
